import { createAsyncThunk } from '@reduxjs/toolkit';
import type { Community } from '@stash/shared-types';
import CommunityService from '../services/community-service';
// Type-only import so we can give the thunk a useful fulfilled-payload type
// without creating a runtime import cycle (the reducer imports this file).
import type { CommunitiesPayload } from '../reducers/community-reducer';
import log from '@/services/log';

interface ServiceError {
  status?: number;
  message?: string;
}

interface FetchCommunitiesParams {
  privacyFilter?: string;
  limit?: number;
  offset?: number;
}

interface UpdateCommunityParams {
  communityId: string;
  updates: Partial<Community>;
}

interface UploadImageParams {
  communityId: string;
  imageType: string;
  imageData: string;
  fileName: string;
}

interface MemberParams {
  communityId: string;
  stasherId: string;
  role?: string;
}

interface RemoveMemberParams {
  communityId: string;
  stasherId: string;
}

interface VendorParams {
  communityId: string;
  vendorId: string;
}

interface DeleteImageParams {
  communityId: string;
  imageType: string;
  imageId: string;
}

export const fetchCommunityAsync = createAsyncThunk<Community, string, { rejectValue: string }>(
  'community/fetchCommunityAsync',
  async (communityId, { rejectWithValue }) => {
    try {
      const communityService = new CommunityService();
      const community = await communityService.getCommunityById(communityId);
      return community as Community;
    } catch (err) {
      const error = err as ServiceError;
      log.error('Failed to fetch community:', error);
      return rejectWithValue(error.message || 'Failed to fetch community');
    }
  },
);

export const fetchCommunitiesAsync = createAsyncThunk<CommunitiesPayload, FetchCommunitiesParams, { rejectValue: string }>(
  'community/fetchCommunitiesAsync',
  async ({ privacyFilter, limit, offset } = {}, { rejectWithValue }) => {
    try {
      const communityService = new CommunityService();
      const communities = await communityService.listCommunities(privacyFilter as null | undefined, limit, offset);
      // listCommunities returns Promise<unknown>; the rest of the app treats it
      // as CommunitiesPayload, so we assert that contract at this boundary.
      return communities as CommunitiesPayload;
    } catch (err) {
      const error = err as ServiceError;
      log.error('Failed to fetch communities:', error);
      return rejectWithValue(error.message || 'Failed to fetch communities');
    }
  },
);

export const updateCommunityAsync = createAsyncThunk<Community, UpdateCommunityParams, { rejectValue: string }>(
  'community/updateCommunityAsync',
  async ({ communityId, updates }, { rejectWithValue }) => {
    try {
      const communityService = new CommunityService();
      const updatedCommunity = await communityService.updateCommunity(communityId, updates);
      return updatedCommunity as Community;
    } catch (err) {
      const error = err as ServiceError;
      log.error('Failed to update community:', error);

      let errorMessage = 'Failed to update community';
      if (error.status === 403) {
        errorMessage = 'You do not have permission to edit this community';
      } else if (error.status === 404) {
        errorMessage = 'Community not found';
      } else if (error.status === 422) {
        errorMessage = error.message || 'Invalid community data';
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export const uploadCommunityImageAsync = createAsyncThunk<Community, UploadImageParams, { rejectValue: string }>(
  'community/uploadCommunityImageAsync',
  async ({ communityId, imageType, imageData, fileName }, { rejectWithValue }) => {
    try {
      const communityService = new CommunityService();
      const updatedCommunity = await communityService.uploadImage(communityId, imageType, imageData, fileName as unknown as null);
      return updatedCommunity as Community;
    } catch (err) {
      const error = err as ServiceError;
      log.error('Failed to upload community image:', error);

      let errorMessage = 'Failed to upload image';
      if (error.status === 403) {
        errorMessage = 'You do not have permission to upload images for this community';
      } else if (error.status === 413) {
        errorMessage = 'Image file is too large';
      } else if (error.status === 422) {
        errorMessage = error.message || 'Invalid image data';
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export const addCommunityMemberAsync = createAsyncThunk<Community, MemberParams, { rejectValue: string }>(
  'community/addCommunityMemberAsync',
  async ({ communityId, stasherId, role }, { rejectWithValue }) => {
    try {
      const communityService = new CommunityService();
      const updatedCommunity = await communityService.addMember(communityId, stasherId, role);
      return updatedCommunity as Community;
    } catch (err) {
      const error = err as ServiceError;
      log.error('Failed to add community member:', error);

      let errorMessage = 'Failed to add member';
      if (error.status === 403) {
        errorMessage = 'You do not have permission to add members to this community';
      } else if (error.status === 404) {
        errorMessage = 'Community or stasher not found';
      } else if (error.status === 422) {
        errorMessage = error.message || 'Invalid member data';
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export const removeCommunityMemberAsync = createAsyncThunk<Community, RemoveMemberParams, { rejectValue: string }>(
  'community/removeCommunityMemberAsync',
  async ({ communityId, stasherId }, { rejectWithValue }) => {
    try {
      const communityService = new CommunityService();
      const updatedCommunity = await communityService.removeMember(communityId, stasherId);
      return updatedCommunity as Community;
    } catch (err) {
      const error = err as ServiceError;
      log.error('Failed to remove community member:', error);

      let errorMessage = 'Failed to remove member';
      if (error.status === 403) {
        errorMessage = 'You do not have permission to remove members from this community';
      } else if (error.status === 404) {
        errorMessage = 'Community or member not found';
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export const addCommunityVendorAsync = createAsyncThunk<Community, VendorParams, { rejectValue: string }>(
  'community/addCommunityVendorAsync',
  async ({ communityId, vendorId }, { rejectWithValue }) => {
    try {
      const communityService = new CommunityService();
      const updatedCommunity = await communityService.addVendor(communityId, vendorId);
      return updatedCommunity as Community;
    } catch (err) {
      const error = err as ServiceError;
      log.error('Failed to add community vendor:', error);

      let errorMessage = 'Failed to add vendor';
      if (error.status === 403) {
        errorMessage = 'You do not have permission to add vendors to this community';
      } else if (error.status === 404) {
        errorMessage = 'Community or vendor not found';
      } else if (error.status === 422) {
        errorMessage = error.message || 'Invalid vendor data';
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export const removeCommunityVendorAsync = createAsyncThunk<Community, VendorParams, { rejectValue: string }>(
  'community/removeCommunityVendorAsync',
  async ({ communityId, vendorId }, { rejectWithValue }) => {
    try {
      const communityService = new CommunityService();
      const updatedCommunity = await communityService.removeVendor(communityId, vendorId);
      return updatedCommunity as Community;
    } catch (err) {
      const error = err as ServiceError;
      log.error('Failed to remove community vendor:', error);

      let errorMessage = 'Failed to remove vendor';
      if (error.status === 403) {
        errorMessage = 'You do not have permission to remove vendors from this community';
      } else if (error.status === 404) {
        errorMessage = 'Community or vendor not found';
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export const deleteCommunityImageAsync = createAsyncThunk<Community, DeleteImageParams, { rejectValue: string }>(
  'community/deleteCommunityImageAsync',
  async ({ communityId, imageType, imageId }, { rejectWithValue }) => {
    try {
      const communityService = new CommunityService();
      const updatedCommunity = await communityService.deleteImage(communityId, imageType, imageId as unknown as null);
      return updatedCommunity as Community;
    } catch (err) {
      const error = err as ServiceError;
      log.error('Failed to delete community image:', error);

      let errorMessage = 'Failed to delete image';
      if (error.status === 403) {
        errorMessage = 'You do not have permission to delete images from this community';
      } else if (error.status === 404) {
        errorMessage = 'Community or image not found';
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export { clearError } from '../reducers/community-reducer';
